// Öffentliche Speiseplan-Seite (/speiseplan). Standorte, Gerichte und die
// Tageszuordnung (welches Gericht an welchem Tag) kommen alle live aus dem Backend
// (siehe menuApi.js/dailyMenuApi.js). Die Zuordnung wiederholt sich automatisch
// alle 14 Tage - das wird im Admin-Editor gepflegt.
//
// Layout: der heutige Tag steht groß oben (TodayCard), die nächsten 13 Tage
// stehen kompakt darunter in einem Raster (UpcomingDayCard).
import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Leaf, Flame, MapPin, WheatOff } from "lucide-react";
import styles from "./Speiseplan.module.css";
import { LOCATION_ID, buildDateWindow, formatPrice } from "../../data/mealPlan";
import { loadLocations, loadMenuData } from "../../services/menuApi";
import { loadDailyPlan, resolveCycleSlots } from "../../services/dailyMenuApi";

const weekdayFormatter = new Intl.DateTimeFormat("de-DE", { weekday: "long" });
const weekdayShortFormatter = new Intl.DateTimeFormat("de-DE", { weekday: "short" });
const dateFormatter = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" });
// Für die Anzeige des Gültigkeitszeitraums (mit Jahr), oben im Seitenkopf.
const rangeFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

// Kleines Label für Eigenschaften wie vegetarisch oder glutenfrei.
function TagBadge({ tag }) {
  const lowerTag = tag.toLowerCase();
  const Icon = lowerTag.includes("scharf")
    ? Flame
    : lowerTag.includes("gluten")
    ? WheatOff
    : Leaf;

  return (
    <span className={styles.tag}>
      <Icon size={13} />
      {tag}
    </span>
  );
}

function DishRow({ label, dish }) {
  if (!dish) {
    return null;
  }

  return (
    <div className={styles.dishRow}>
      <span className={styles.dishLabel}>{label}</span>
      <div className={styles.dishInfo}>
        {dish.imageUrl && (
          <img className={styles.dishImage} src={dish.imageUrl} alt={dish.name} />
        )}
        {/* Name und Preis bewusst als eigenes, eng zusammengehöriges Paar - sonst
            reißt justify-content:space-between sie bei breiten Karten mit wenig
            Text weit auseinander und es entsteht eine hässliche Lücke. */}
        <div className={styles.dishText}>
          <div className={styles.dishTextHead}>
            <h4>{dish.name}</h4>
            <strong>{formatPrice(dish.priceCents, dish.currency)}</strong>
          </div>
          <p>{dish.description}</p>
        </div>
      </div>
      {dish.tags.length > 0 && (
        <div className={styles.tags}>
          {dish.tags.slice(0, 3).map((tag) => (
            <TagBadge key={tag} tag={tag} />
          ))}
        </div>
      )}
    </div>
  );
}

// Löst die im Tagesplan hinterlegten Gericht-IDs in echte Gerichte auf. Der 14-Tage-Plan
// wiederholt sich automatisch (siehe resolveCycleSlots), deshalb schauen wir hier nicht
// nur auf das exakte Datum, sondern ggf. auch 14/28/... Tage zurück.
// Ein Gericht, das inzwischen ausgeblendet oder gelöscht wurde, fällt einfach weg.
function resolveDaySlots(date, dailyPlan, dishesById) {
  const { slots } = resolveCycleSlots(dailyPlan, date);

  return {
    fixed: dishesById.get(slots.fixedId) || null,
    special1: dishesById.get(slots.special1Id) || null,
    special2: dishesById.get(slots.special2Id) || null,
  };
}

function TodayCard({ date, slots, isClosed }) {
  const hasDishes = slots.fixed || slots.special1 || slots.special2;

  return (
    <article className={styles.todayCard}>
      <header className={styles.todayHeader}>
        <span className={styles.todayEyebrow}>Heute</span>
        <h2>{weekdayFormatter.format(date)}</h2>
        <span>{dateFormatter.format(date)}</span>
      </header>

      {isClosed ? (
        <div className={styles.emptyState}>
          <CalendarDays size={22} />
          <p>Heute geschlossen.</p>
        </div>
      ) : hasDishes ? (
        <div className={styles.todayDishes}>
          <DishRow label="Tagesgericht" dish={slots.fixed} />
          <DishRow label="Sondergericht 1" dish={slots.special1} />
          <DishRow label="Sondergericht 2" dish={slots.special2} />
        </div>
      ) : (
        <div className={styles.emptyState}>
          <CalendarDays size={22} />
          <p>Für heute ist noch kein Essen eingetragen.</p>
        </div>
      )}
    </article>
  );
}

function UpcomingDayCard({ date, slots, isClosed }) {
  const dishes = [slots.fixed, slots.special1, slots.special2].filter(Boolean);

  return (
    <article className={styles.upcomingCard}>
      <div className={styles.upcomingDate}>
        <span>{weekdayShortFormatter.format(date)}</span>
        <strong>{dateFormatter.format(date)}</strong>
      </div>
      {isClosed ? (
        <p className={styles.upcomingEmpty}>Geschlossen.</p>
      ) : dishes.length === 0 ? (
        <p className={styles.upcomingEmpty}>Noch kein Essen eingetragen.</p>
      ) : (
        <ul className={styles.upcomingList}>
          {dishes.map((dish) => (
            <li key={dish.id}>
              <span>{dish.name}</span>
              <strong>{formatPrice(dish.priceCents, dish.currency)}</strong>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function Speiseplan() {
  const [dishes, setDishes] = useState([]);
  const [dailyPlan, setDailyPlan] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(LOCATION_ID);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    loadLocations()
      .then((locationData) => {
        if (isMounted) {
          setLocations(locationData);
          setSelectedLocation((currentLocation) =>
            locationData.some((location) => location.id === currentLocation)
              ? currentLocation
              : locationData[0]?.id || LOCATION_ID
          );
        }
      })
      .catch((error) => {
        if (isMounted) {
          setErrorMessage(error.message || "Standorte konnten nicht geladen werden.");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setErrorMessage("");

    // Bei Standortwechsel werden Speisekarte und Tageszuordnung parallel geholt.
    Promise.all([loadMenuData(selectedLocation), loadDailyPlan(selectedLocation)])
      .then(([data, plan]) => {
        if (isMounted) {
          setDishes(data);
          setDailyPlan(plan);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setDishes([]);
          setDailyPlan({});
          setErrorMessage(error.message || "Speiseplan konnte nicht geladen werden.");
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedLocation]);

  const dishesById = useMemo(
    () => new Map(dishes.map((dish) => [dish.id, dish])),
    [dishes]
  );

  // Rollendes Fenster: heute plus die nächsten 13 Tage.
  const dateWindow = useMemo(() => buildDateWindow(14), []);
  const [todayKey, ...upcomingKeys] = dateWindow;
  const lastKey = dateWindow[dateWindow.length - 1];

  // Standort-Einstellung, ob am Wochenende überhaupt bedient wird (siehe
  // LocationForm im Admin-Editor). Ohne bekannten Standort gilt das Wochenende
  // vorsichtshalber als geschlossen, statt fälschlich "offen" anzunehmen.
  const currentLocation = locations.find((location) => location.id === selectedLocation);
  function isWeekendClosed(dateKey) {
    const weekday = new Date(`${dateKey}T12:00:00`).getDay(); // 0 = Sonntag, 6 = Samstag
    return (
      (weekday === 6 && !currentLocation?.serveSaturday) ||
      (weekday === 0 && !currentLocation?.serveSunday)
    );
  }
  // Sichtbarer Gültigkeitszeitraum, damit klar ist, für welchen Zeitraum der Plan gilt.
  const validityRange = `${rangeFormatter.format(
    new Date(`${todayKey}T12:00:00`)
  )} – ${rangeFormatter.format(new Date(`${lastKey}T12:00:00`))}`;

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <p className={styles.eyebrow}>Foodosia Betriebskantine</p>
        <h1>Speiseplan</h1>
        <p>
          Unser Tagesgericht und zwei Sondergerichte, immer aktuell für die
          nächsten zwei Wochen und je Standort gepflegt.
        </p>
        <p className={styles.validityRange}>
          <CalendarDays size={15} />
          Gültig: {validityRange}
        </p>
        <label className={styles.locationSelect}>
          <MapPin size={18} />
          <span>Standort</span>
          <select
            value={selectedLocation}
            onChange={(event) => setSelectedLocation(event.target.value)}
          >
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      {errorMessage ? (
        <div className={styles.errorBox}>{errorMessage}</div>
      ) : isLoading ? (
        <div className={styles.loading}>Speiseplan wird geladen...</div>
      ) : dishes.length === 0 ? (
        <div className={styles.emptyState}>
          <CalendarDays size={24} />
          <p>Für diesen Standort ist aktuell kein Essen eingetragen.</p>
        </div>
      ) : (
        <>
          <TodayCard
            date={new Date(`${todayKey}T12:00:00`)}
            slots={resolveDaySlots(todayKey, dailyPlan, dishesById)}
            isClosed={isWeekendClosed(todayKey)}
          />

          <section className={styles.upcomingGrid}>
            {upcomingKeys.map((dateKey) => (
              <UpcomingDayCard
                key={dateKey}
                date={new Date(`${dateKey}T12:00:00`)}
                slots={resolveDaySlots(dateKey, dailyPlan, dishesById)}
                isClosed={isWeekendClosed(dateKey)}
              />
            ))}
          </section>
        </>
      )}
    </main>
  );
}

export default Speiseplan;
